import { doc, setDoc, Timestamp } from "firebase/firestore";
import { deleteObject, ref } from "firebase/storage";
import { auth, db, storage } from "../../firebaseConfig";
import {
  copyReferenceAttachmentToArchive,
  uploadReferenceAttachment,
} from "./saveWorkHistoryReference";
import { logReferenceAudit } from "./referenceAuditLog";

const toTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (typeof value?.toDate === "function") return Timestamp.fromDate(value.toDate());
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
};

export const savePersonalReference = async ({
  applicationId,
  referenceId,
  sourceKey,
  referenceDetails,
  referee,
  form,
  referenceKind = "personal",
}) => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error("You must be logged in to save a reference.");

  const attachments = await Promise.all(
    (form.attachments || []).map((file) =>
      uploadReferenceAttachment({
        file,
        userId: user.uid,
        applicationId,
        referenceId,
      }),
    ),
  );
  const retainedPaths = new Set(attachments.map((item) => item?.path));
  const removedAttachments = (referee?.referenceAttachments || []).filter(
    (item) =>
      item?.path &&
      item.source !== "reference-consent-form" &&
      !retainedPaths.has(item.path),
  );
  const archived = await Promise.all(
    removedAttachments.map(copyReferenceAttachmentToArchive),
  );

  const receivedReferenceAttachments = await Promise.all(
    (form.receivedReferenceAttachments || []).map((file) =>
      uploadReferenceAttachment({
        file,
        userId: user.uid,
        applicationId,
        referenceId,
        attachmentFolder: "received",
      }),
    ),
  );

  const isTradeReference = referenceKind === "trade";
  const isAccountantReference = referenceKind === "accountant";
  const isEducationalReference = referenceKind === "educational";
  const savedReferee = {
    ...(referee || {}),
    ...(isAccountantReference
      ? {
          accountantReferenceName: form.fullName,
          telephone: form.contactNumber,
          emailAddress: form.recipients,
          accountantFrom: toTimestamp(form.accountantFrom),
          from: toTimestamp(form.accountantFrom),
          accountantReferenceQuestions: form.questions,
        }
      : isTradeReference
      ? {
          tradeName: form.fullName,
          companyName: form.companyName,
          tradeEmailAddress: form.recipients,
          datesOfTradingActivities: [form.tradingFrom, form.tradingTo]
            .filter(Boolean)
            .join(" - "),
          tradingFrom: form.tradingFrom,
          tradingTo: form.tradingTo,
          tradeReferenceQuestions: form.questions,
        }
      : isEducationalReference
      ? {
          educationalEmailAddress: form.recipients,
          educationFrom: toTimestamp(form.educationFrom),
          educationTo: toTimestamp(form.educationTo),
          from: toTimestamp(form.educationFrom),
          to: toTimestamp(form.educationTo),
          educationDetails: form.educationDetails,
          educationalReferenceQuestions: form.questions,
        }
      : {
          refereeName: form.fullName,
          refereeEmailAddress: form.recipients,
          relationshipToReferee: form.relationship,
          datesKnown: form.datesKnown,
          personalReferenceQuestions: form.questions,
        }),
    ...(!isAccountantReference && !isEducationalReference
      ? { refereePhoneNumber: form.contactNumber }
      : {}),
    referenceStatus: form.status,
    referenceSent: toTimestamp(form.referenceSent),
    referenceFollowUp: form.status !== "reference-received" && form.followUpTimeframe && form.referenceSent
      ? toTimestamp(form.referenceFollowUp)
      : null,
    lastFollowedUpOn: toTimestamp(form.lastFollowedUpOn),
    referenceReceived: toTimestamp(form.referenceReceived),
    followUpTimeframe: form.followUpTimeframe,
    followUpSetDate: toTimestamp(form.followUpSetDate),
    skipReferenceFollowUp: Boolean(form.skipReferenceFollowUp),
    internalAdminNotes: form.internalAdminNotes,
    adviserPermission: form.adviserPermission,
    referenceCc: form.cc,
    referenceBcc: form.bcc,
    referenceSubject: form.subject,
    referenceSender: form.sender || "",
    additionalReferenceQuestion: form.additionalQuestion,
    referenceMessageBody: form.messageBody || "",
    referenceAttachments: attachments,
    receivedReferenceAttachments,
    archivedReferenceAttachments: [
      ...(referee?.archivedReferenceAttachments || []),
      ...archived,
    ],
    referenceUpdatedAt: Timestamp.now(),
    referenceUpdatedBy: user.email || user.uid,
  };

  if (isAccountantReference) {
    await setDoc(
      doc(
        db,
        "Recruitment",
        applicationId,
        "Application Details",
        "Accountant Details",
      ),
      savedReferee,
      { merge: true },
    );
    await logReferenceAudit({
      applicationId,
      referenceId,
      referenceKind,
      action: "reference_saved",
      before: referee || null,
      after: savedReferee,
      details: {
        form,
        confirmations: {
          detailsConfirmedBeforeSave: Boolean(form.saveConfirmed),
        },
      },
    });
    await Promise.allSettled(
      removedAttachments.map((item) => deleteObject(ref(storage, item.path))),
    );
    return { savedReferee, updatedReferees: savedReferee };
  }

  const refereeField = isTradeReference
    ? "tradeReferees"
    : isEducationalReference
      ? "educationalReferees"
      : "personalReferees";
  const currentReferees = referenceDetails?.[refereeField] || [];
  let updatedReferees;
  if (Array.isArray(currentReferees)) {
    updatedReferees = [...currentReferees];
    updatedReferees[Number(sourceKey)] = savedReferee;
  } else {
    updatedReferees = { ...currentReferees, [sourceKey]: savedReferee };
  }

  await setDoc(
    doc(
      db,
      "Recruitment",
      applicationId,
      "Application Details",
      "Accountant Details",
    ),
    { ...referenceDetails, [refereeField]: updatedReferees },
    { merge: true },
  );

  await logReferenceAudit({
    applicationId,
    referenceId,
    referenceKind,
    action: "reference_saved",
    before: referee || null,
    after: savedReferee,
    details: {
      form,
      confirmations: {
        detailsConfirmedBeforeSave: Boolean(form.saveConfirmed),
      },
    },
  });

  await Promise.allSettled(
    removedAttachments.map((item) => deleteObject(ref(storage, item.path))),
  );
  return { savedReferee, updatedReferees };
};
